import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import AsseRequestModal from "./assetRequestModel";
import { getEmployeeByUserId } from "../../../core/data/redux/employee/employeeAction";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import { exportToCSV, exportToExcel, exportToPDF, } from "../../../core/common/GlobalFunctions/exportTableData";
import FilterHeader from "../../mainMenu/requestDashboard/FilterHeader";
import { getStatusCards } from "../../mainMenu/requestDashboard/StatusSummaryCards";
import { deleteAssetRequest, fetchAllAssetRequests, fetchAssetRequestsByEmployeeId, } from "../../../core/data/redux/asset/assetRequest/assetRequestAction";
import { message } from "antd";
import { formatDate } from "../../../core/utils/configFormatters";
import { useAppConfig } from "../../../customhooks/useAppConfig";
import { decryptData } from "../../../core/utils/encryptUtils";
import { Tooltip } from "reactstrap";

const AssetRequest = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const { user } = useSelector((state: any) => state.auth);
  const { employeesbyUser } = useSelector((state: any) => state.employee);
  const { AssetByEmployee = [] } = useSelector((state: any) => state.asset.assetRequest);
  const employeeId = employeesbyUser?.employeeId;
  const assetRequestId = selectedRowData?.Id;


  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");

  useEffect(() => {
    if (!employeesbyUser?.employeeId && user?.UserId) {
      dispatch(getEmployeeByUserId({ userId: user.UserId }));
    }
    dispatch(fetchAssetRequestsByEmployeeId(employeesbyUser?.employeeId));
  }, [dispatch, employeesbyUser, user]);



  const handleOpenModal = (type: "edit" | "create", data?: any) => {
    setModalType(type);
    setSelectedRowData(data || null);
    setIsModalVisible(true);
  };

  const handleDeleteRequest = async (id: number) => {
    try {
      const result = await dispatch(deleteAssetRequest(id));
      if (result.meta.requestStatus === "fulfilled") {
        message.success("Deleted successfully");
        setIsDeleteModalVisible(false);
        await dispatch(fetchAssetRequestsByEmployeeId(employeesbyUser?.employeeId));
        setTableKey((prev) => prev + 1);
      } else {
        message.error(result.payload.error);
      }
    } catch {
      message.error("Something went wrong");
    }
  };

  const filteredData = useMemo(() => {
    const filters =
      selectedStatus && selectedStatus !== "All"
        ? { AssetRequestStatus: selectedStatus }
        : {};
    return filterData(
      AssetByEmployee || [],
      filters,
      "CreatedAt",
      "CreatedAt",
      selectedSort
    );
  }, [AssetByEmployee, selectedStatus, selectedSort]);

  const exportAssetRequests = filteredData.map((item: any) => ({
    RequestNumber: item.RequestNumber,
    RequestDescription: item.RequestDescription || "N/A",
    RejectDescription: item.RejectDescription || "N/A",
    RequestDate: item.RequestDate ? new Date(item.RequestDate).toISOString().split("T")[0] : "N/A",
    Quantity: item.Quantity ?? 0,
    AssetRequestStatus: item.AssetRequestStatus || "N/A",
    CreatedAt: item.CreatedAt ? new Date(item.CreatedAt).toISOString().split("T")[0] : "N/A",
    Name: item.Name || "N/A",
    EmployeeName: item.EmployeeName || "N/A",
    AssetCategoryName: item.AssetCategoryName || "N/A",
  }));


  useEffect(() => {
    setTableKey((prev) => prev + 1);
  }, [selectedStatus, selectedSort, AssetByEmployee]);

  const config = useAppConfig();
  if (!config) return null;

  const columns = [
    {
      title: "Request Id",
      dataIndex: "RequestNumber",
      sorter: (a: any, b: any) =>
        a.RequestNumber.localeCompare(b.RequestNumber),
      render: (text: string) => (
        <span className="text-danger">{text}</span>
      ),
    },
    {
      title: "Employee",
      dataIndex: "EmployeeName",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center">
          <div className="avatar avatar-md">
            <img src={record.EmployeeImage ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"} alt="img" className="img-fluid rounded-circle" />
          </div>
          <div className="ms-2">
            {record.EmployeeName}
          </div>
        </div>
      ),
    },
    {
      title: "Asset Category",
      dataIndex: "AssetCategoryName",
      sorter: (a: any, b: any) =>
        a.AssetCategoryName.localeCompare(b.AssetCategoryName),
    },
    {
      title: "Asset Name",
      dataIndex: "Name",
      sorter: (a: any, b: any) => a.Name.localeCompare(b.Name),
    },
    {
      title: "Quantity",
      dataIndex: "Quantity",
      sorter: (a: any, b: any) => a.Quantity - b.Quantity,
    },
    {
      title: "Request Date",
      dataIndex: "RequestDate",
      render: (text: string) => formatDate(text, config),
      sorter: (a: any, b: any) =>
        new Date(a.RequestDate).getTime() - new Date(b.RequestDate).getTime(),
    },
    {
      title: "Status",
      dataIndex: "AssetRequestStatus",
      render: (status: string, record: any) => {
        const statusClass =
          {
            Pending: "badge-warning",
            Approved: "badge-success",
            Rejected: "badge-danger",
          }[status] || "";

        const isRejected = status === "Rejected";
        const tooltipText = isRejected ? record.RejectDescription || "No reason provided" : "";

        return (
          <span
            className={`badge ${statusClass} d-inline-flex align-items-center badge-xs ${isRejected ? 'rejected-tooltip-trigger' : ''
              }`}
            title={tooltipText}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
          >
            <i className="ti ti-point-filled me-1" />
            {status}
          </span>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "RequestDescription",
      render: (text: string) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: 200 }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (_: string, record: any) => {
        const status = record.AssetRequestStatus;
        const isInactive = status === "Approved" || status === "Rejected";
        const isEditDisabled = !canEdit || isInactive;
        const isDeleteDisabled = !canDelete || isInactive;

        return (
          <div className="action-icon d-inline-flex">
            {/* Edit Button */}
            <button
              className="me-2"
              onClick={() => {
                if (!isEditDisabled) {
                  handleOpenModal("edit", record);
                }
              }}
              disabled={isEditDisabled}
              style={{
                cursor: isEditDisabled ? "not-allowed" : "pointer",
                opacity: isEditDisabled ? 0.5 : 1,
              }}
              title={!canEdit ? "Permission Denied" : "Edit"}
            >
              <i className="ti ti-edit" />
            </button>

            {/* Delete Button */}
            <button
              className="me-2"
              onClick={() => {
                if (!isDeleteDisabled) {
                  setSelectedRowData(record);
                  setIsDeleteModalVisible(true);
                }
              }}
              disabled={isDeleteDisabled}
              style={{
                cursor: isDeleteDisabled ? "not-allowed" : "pointer",
                opacity: isDeleteDisabled ? 0.5 : 1,
              }}
              title={
                !canDelete
                  ? "Permission Denied"
                  : isInactive
                    ? "Inactive Record"
                    : "Delete"
              }
            >
              <i className="ti ti-trash" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div>
              <h2 className="mb-1">Assets Request</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Asset</li>
                  <li className="breadcrumb-item active">Request</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex align-items-center flex-wrap">
              {canExport && (
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-file-export me-1" /> Export
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <button
                        onClick={() =>
                          exportToCSV(exportAssetRequests, "asset_request_data")
                        }
                        className="dropdown-item"
                      >
                        Export as CSV
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() =>
                          exportToExcel(exportAssetRequests, "asset_request_data")
                        }
                        className="dropdown-item"
                      >
                        Export as Excel
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() =>
                          exportToPDF(exportAssetRequests, "asset_request_data")
                        }
                        className="dropdown-item"
                      >
                        Export as PDF
                      </button>
                    </li>
                  </ul>
                </div>
              )}
              {canAdd && (
                <button
                  className="btn btn-primary d-flex align-items-center"
                  onClick={() => handleOpenModal("create")}
                >
                  <i className="ti ti-circle-plus me-2" /> Add Request
                </button>
              )}
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          <div className="row mb-4">
            {getStatusCards(AssetByEmployee, "AssetRequestStatus")}
          </div>
          {/* Filters */}
          <div className="card">
            <FilterHeader
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
            />
            <div className="card-body p-0 asset-request-table">
              <Table
                key={tableKey}
                dataSource={filteredData}
                columns={columns}
                Selection={false}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex justify-content-between bg-white p-3 border-top">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>

      {/* Modals */}
      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() => assetRequestId && handleDeleteRequest(assetRequestId)}
      />
      <AsseRequestModal
        modalType={modalType}
        isModalVisible={isModalVisible}
        handleCancel={() => setIsModalVisible(false)}
        employeeId={employeeId}
        setTableKey={setTableKey}
        assetRequestId={assetRequestId}
        selectedRowData={selectedRowData}
      />
    </>
  );
};

export default AssetRequest;
